import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Modal-dialog focus management for portaled overlays.
 *
 * While `active`, this:
 *  - moves focus to the first focusable element inside `ref` on mount
 *    (falling back to the container itself — give it `tabIndex={-1}`),
 *  - traps Tab / Shift+Tab so focus cycles within the overlay,
 *  - restores focus to the previously-focused element on unmount/deactivate.
 *
 * Pair with `role="dialog"` (or `role="alertdialog"`), `aria-modal="true"`,
 * and an accessible name (`aria-label` / `aria-labelledby`) on the same node.
 */
export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  active = true,
  /** Element to focus on mount instead of the first focusable — e.g. the
   *  Cancel button of a destructive confirm dialog. */
  initialFocusRef?: React.RefObject<HTMLElement | null>,
  options?: {
    /**
     * Walk Tab / Shift+Tab through the trap's OWN focus order (fields AND
     * buttons, DOM order, wrapping) instead of only wrapping at the ends.
     * WebKit with Safari's default setting does not stop on buttons, so for
     * it the last tabbable of a dialog can be a field the trap does not see
     * as "last" — Tab then left for <body> and the page behind. Opt-in: a
     * dialog with radio groups or positive tabindex keeps native order.
     */
    cycleTab?: boolean;
  },
): void {
  const cycleTab = options?.cycleTab ?? false;
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    // Something inside already claimed focus — a create wizard's autoFocus
    // field, typically. Stealing it back would move the caret out of the field
    // the user is meant to start typing in.
    if (!node.contains(document.activeElement)) {
      (initialFocusRef?.current ?? focusables()[0] ?? node).focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) {
        // Nothing focusable but the container — keep focus inside.
        e.preventDefault();
        node.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const activeEl = document.activeElement;
      if (cycleTab) {
        e.preventDefault();
        const index = els.indexOf(activeEl as HTMLElement);
        const next =
          index === -1
            ? e.shiftKey
              ? last
              : first
            : els[(index + (e.shiftKey ? -1 : 1) + els.length) % els.length];
        next.focus();
        return;
      }
      if (e.shiftKey && (activeEl === first || activeEl === node)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [ref, active, cycleTab]);
}
